import * as React from "react";
import { Activity, BookOpen, Building2, CalendarDays, Download, Gauge, LifeBuoy, RefreshCw, Sparkles } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  downloadOrgInsightUsageCsv,
  fetchInsightBudget,
  fetchOrgInsightUsage,
  type InsightBudgetStatus,
  type OrgInsightUsage,
} from "../../lib/api/insights";
import { fetchOrganizations } from "../../lib/api/users";
import { formatUsd } from "../products/insights/InsightCard";
import { PilotMetricsSection } from "./PilotMetricsSection";
import * as s from "./InsightsUsagePage.css";

const SECTION_METRICS = "system-insights-metrics";
const SECTION_BUDGET = "system-insights-budget";
const SECTION_USAGE = "system-insights-usage";

type PeriodChoice = "current" | "previous";

function periodFor(choice: PeriodChoice): { start?: string; end?: string } {
  if (choice === "current") return {};
  const now = new Date();
  return {
    start: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)).toISOString(),
    end: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString(),
  };
}

function level(spent: number, limit: number): "ok" | "warn" | "over" {
  if (limit <= 0 || spent >= limit) return "over";
  return spent / limit >= 0.8 ? "warn" : "ok";
}

function BudgetMeter({ label, spent, limit }: { label: string; spent: number; limit: number }) {
  const pct = limit > 0 ? Math.min(100, (spent / limit) * 100) : 100;
  return (
    <div className={s.budgetCard}>
      <Text level="body-small" color="muted">
        {label}
      </Text>
      <span className={s.budgetFigure}>
        {formatUsd(spent)} / {formatUsd(limit)}
      </span>
      <div className={s.barTrack} role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={limit} aria-valuenow={spent}>
        <div className={s.barFill} data-level={level(spent, limit)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function InsightsUsagePage() {
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("system-insights");
  const navigate = useNavigate();

  const [period, setPeriod] = React.useState<PeriodChoice>("current");
  const [budget, setBudget] = React.useState<InsightBudgetStatus | null>(null);
  const [usage, setUsage] = React.useState<OrgInsightUsage | null>(null);
  const [orgNames, setOrgNames] = React.useState<Map<string, string>>(new Map());
  const [refreshCount, setRefreshCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [exporting, setExporting] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [b, u] = await Promise.all([fetchInsightBudget(token), fetchOrgInsightUsage(token, periodFor(period))]);
      setBudget(b);
      setUsage(u);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pro.system.insights.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [token, period, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (!token) return;
    fetchOrganizations(token, { includeDeleted: true })
      .then((orgs) => setOrgNames(new Map(orgs.map((o) => [o.id, o.name]))))
      .catch(() => {});
  }, [token]);

  const handleExport = async () => {
    if (!token) return;
    setExporting(true);
    try {
      await downloadOrgInsightUsageCsv(token, periodFor(period));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pro.system.insights.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  const sectionLayout = bindSectionLayout([
    { id: SECTION_METRICS, label: t("pro.system.insights.metrics.title"), icon: <Activity size={14} /> },
    { id: SECTION_BUDGET, label: t("pro.system.insights.budget"), icon: <Gauge size={14} /> },
    { id: SECTION_USAGE, label: t("pro.system.insights.usageByOrg"), icon: <Building2 size={14} /> },
  ]);

  const orgRows = usage?.organizations ?? [];
  const periodLabel = usage
    ? new Date(usage.period.start).toLocaleDateString(undefined, { year: "numeric", month: "long", timeZone: "UTC" })
    : "";

  return (
    <DetailPageLayout
      header={{
        media: <Sparkles size={48} />,
        title: t("pro.system.insights.title"),
        subtitle: t("pro.system.insights.subtitle"),
      }}
      contextActions={[
        {
          id: "guidance",
          label: t("pro.system.insights.guidanceLink"),
          icon: <BookOpen size={14} />,
          onClick: () => navigate({ to: "/system/insights/guidance" }),
        },
        {
          id: "support",
          label: t("pro.system.insights.supportLink"),
          icon: <LifeBuoy size={14} />,
          onClick: () => navigate({ to: "/system/insights/support", search: { ref: undefined } }),
        },
        {
          id: "period",
          label: period === "current" ? t("pro.system.insights.showPrevious") : t("pro.system.insights.showCurrent"),
          icon: <CalendarDays size={14} />,
          onClick: () => setPeriod((p) => (p === "current" ? "previous" : "current")),
          disabled: loading,
        },
        {
          id: "export",
          label: exporting ? t("pro.system.insights.exporting") : t("pro.system.insights.exportCsv"),
          icon: <Download size={14} />,
          onClick: () => void handleExport(),
          disabled: exporting || loading,
        },
        {
          id: "refresh",
          label: t("pro.system.insights.refresh"),
          icon: <RefreshCw size={14} />,
          onClick: () => {
            setRefreshCount((n) => n + 1);
            void load();
          },
          disabled: loading,
        },
      ]}
      {...sectionLayout}
    >
      {error ? <Callout variant="error">{error}</Callout> : null}

      <CollapsibleSection
        id={SECTION_METRICS}
        title={t("pro.system.insights.metrics.title")}
        subtitle={periodLabel}
        icon={<Activity size={20} />}
        expanded={isExpanded(SECTION_METRICS)}
        onToggleExpanded={() => toggle(SECTION_METRICS)}
      >
        <PilotMetricsSection
          key={refreshCount}
          token={token}
          period={periodFor(period)}
          organizations={[...orgNames].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))}
        />
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_BUDGET}
        title={t("pro.system.insights.budget")}
        icon={<Gauge size={20} />}
        expanded={isExpanded(SECTION_BUDGET)}
        onToggleExpanded={() => toggle(SECTION_BUDGET)}
      >
        <div className={s.sectionInner}>
          {loading && !budget ? (
            <AuthTransitionScreen variant="compact" message={t("pro.system.insights.loading")} />
          ) : budget ? (
            <>
              {!budget.enabled ? (
                <Callout variant="info">
                  {budget.redisAvailable
                    ? t("pro.system.insights.killSwitchOff")
                    : t("pro.system.insights.noRedis")}
                </Callout>
              ) : null}
              <div className={s.budgetGrid}>
                <BudgetMeter
                  label={t("pro.system.insights.today")}
                  spent={budget.day.spentUsd}
                  limit={budget.day.limitUsd}
                />
                <BudgetMeter
                  label={t("pro.system.insights.thisMonth")}
                  spent={budget.month.spentUsd}
                  limit={budget.month.limitUsd}
                />
                {budget.areas.map((area) => (
                  <BudgetMeter
                    key={area.area}
                    label={t("pro.system.insights.areaMonth", {
                      area: t(`pro.system.insights.area.${area.area}`),
                      state: area.enabled ? t("pro.system.insights.areaOn") : t("pro.system.insights.areaOff"),
                    })}
                    spent={area.spentUsd}
                    limit={area.limitUsd}
                  />
                ))}
              </div>
              <Text level="body-small" color="muted">
                {t("pro.system.insights.limitsHint")}
              </Text>
            </>
          ) : null}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_USAGE}
        title={t("pro.system.insights.usageByOrg")}
        subtitle={periodLabel}
        icon={<Building2 size={20} />}
        expanded={isExpanded(SECTION_USAGE)}
        onToggleExpanded={() => toggle(SECTION_USAGE)}
      >
        <div className={s.sectionInner}>
          {loading && !usage ? (
            <AuthTransitionScreen variant="compact" message={t("pro.system.insights.loading")} />
          ) : orgRows.length === 0 ? (
            <Text level="body-small" color="muted">
              {t("pro.system.insights.noUsage")}
            </Text>
          ) : (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={s.th}>{t("pro.system.insights.col.organization")}</th>
                    <th className={`${s.th} ${s.numeric}`}>{t("pro.system.insights.col.products")}</th>
                    <th className={`${s.th} ${s.numeric}`}>{t("pro.system.insights.col.calls")}</th>
                    <th className={`${s.th} ${s.numeric}`}>{t("pro.system.insights.col.failed")}</th>
                    <th className={`${s.th} ${s.numeric}`}>{t("pro.system.insights.col.headlines")}</th>
                    <th className={`${s.th} ${s.numeric}`}>{t("pro.system.insights.col.details")}</th>
                    <th className={`${s.th} ${s.numeric}`}>{t("pro.system.insights.col.total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {orgRows.map((org) => (
                    <tr key={org.organizationId ?? "unattributed"}>
                      <td className={s.td}>
                        {org.organizationId
                          ? (orgNames.get(org.organizationId) ?? org.organizationId)
                          : t("pro.system.insights.unattributed")}
                      </td>
                      <td className={`${s.td} ${s.numeric}`}>{org.byProduct.length}</td>
                      <td className={`${s.td} ${s.numeric}`}>{org.totals.count}</td>
                      <td className={`${s.td} ${s.numeric}`}>{org.totals.failed}</td>
                      <td className={`${s.td} ${s.numeric}`}>{formatUsd(org.byTier.headline.costUsd)}</td>
                      <td className={`${s.td} ${s.numeric}`}>{formatUsd(org.byTier.details.costUsd)}</td>
                      <td className={`${s.td} ${s.numeric}`}>{formatUsd(org.totals.costUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CollapsibleSection>
    </DetailPageLayout>
  );
}
